// @ts-strict-ignore
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, OnInit, Output } from '@angular/core';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { TabsetComponent } from 'ngx-bootstrap/tabs';
import { TranslateService } from '@ngx-translate/core';
import { ProjectModel } from '@project/model/project.model';
import { ProjectOptionDefVO } from '@project/model/vo/project.vo';
import { ProjectOptionUtils } from '../util/project-option.utils';
import { CloneUtils } from '@shared/util/clone-utils';
import { ProjectOptionContainer, ProjectOptionDefWrapper } from '@project/components/project/util/project-option-data';
import { LocaleResourceManager } from '@shared/manager/locale-resource.manager';
import { UserComponentPresenter } from '@user/components/user-component.presenter';
import { ProjectComponentPresenter } from '../../project-component.presenter';
import { ProjectUserModel } from '../../../model/project-user.model';

@Component({
  standalone: false,
  templateUrl: `project-option-def.component.html`,
  providers: [TabsetComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'app-project-option-def',
})
export class ProjectOptionDefComponent implements OnInit {
  @Output() public finish = new EventEmitter<void>();

  public projectOptionContainer: ProjectOptionContainer<ProjectOptionDefWrapper> = new ProjectOptionContainer<ProjectOptionDefWrapper>();
  public includeInternalOptions: boolean = false;
  private selectedLangKey: string = null;

  constructor(
    private bsModalRef: BsModalRef,
    private cdRef: ChangeDetectorRef,
    private projectModel: ProjectModel,
    private localeResourceManager: LocaleResourceManager,
    private translateService: TranslateService,
    private projectOptionUtils: ProjectOptionUtils,
    private _userComponentPresenter: UserComponentPresenter,
    private projectComponentPresenter: ProjectComponentPresenter,
    private projectUserModel: ProjectUserModel,
  ) {}

  ngOnInit(): void {
    this.includeInternalOptions = ProjectOptionUtils.canSeeInternalOptions(this.projectModel.user);
    this.selectedLangKey = this.localeResourceManager.currentLanguage.langKey;
    this.getSortedProjectOptions();
  }

  private getSortedProjectOptions(): void {
    const copiedUserOptions: ProjectOptionDefVO[] = [];
    for (const optionDef of this.projectUserModel.projectOptionDefs) {
      if (ProjectOptionUtils.isForcedInvisible(optionDef.identifier, this.projectModel.user)) {
        continue;
      }

      const copiedOption: ProjectOptionDefVO = CloneUtils.cloneProjectOptionDefVO(optionDef);
      copiedUserOptions.push(copiedOption);
    }
    ProjectOptionUtils.sortProjectOptionDefsByOrder(copiedUserOptions);

    const optionContainer: ProjectOptionContainer<ProjectOptionDefWrapper> = new ProjectOptionContainer<ProjectOptionDefWrapper>();
    copiedUserOptions.forEach((option: ProjectOptionDefVO) => {
      const optionWrapper: ProjectOptionDefWrapper = new ProjectOptionDefWrapper();
      optionWrapper.projectOptionDef = option;
      const optionAdded: boolean = optionContainer.addOption(optionWrapper, this.includeInternalOptions);
      if (optionAdded) {
        this.refreshWrapperDisplayData(optionWrapper);
      }
    });
    this.projectOptionContainer = optionContainer;
    this.cdRef.markForCheck();
  }

  public async openLanguageDialog(): Promise<void> {
    const selectedLanguage = await this._userComponentPresenter.presentLanguageComponent(this.selectedLangKey);
    if (selectedLanguage) {
      const currentLang: string = this.translateService.currentLang;
      this.selectedLangKey = selectedLanguage.langKey;
      this.translateService.use(selectedLanguage.langCode).subscribe(() => {
        this.projectOptionContainer.getAll().forEach((wrapper: ProjectOptionDefWrapper) => {
          this.refreshWrapperDisplayData(wrapper);
        });
        this.translateService.use(currentLang);
        this.cdRef.markForCheck();
      });
    }
  }

  public isEditAllowed(projectOption: ProjectOptionDefWrapper): boolean {
    if (!projectOption) {
      return false;
    }
    return projectOption.order >= 0 && parseInt(projectOption.identifier, 10) <= 150;
  }

  public openEdit(projectOption: ProjectOptionDefWrapper): void {
    if (!projectOption || !projectOption.projectOptionDef) {
      return;
    }

    this.projectComponentPresenter
      .openEditProjectOptionDefPopup(
        projectOption.projectOptionDef,
        this.projectOptionContainer.getAll().map((wrapper: ProjectOptionDefWrapper) => wrapper.projectOptionDef),
        this.selectedLangKey,
        projectOption.displayName
      )
      .then((success: boolean) => {
        if (success) {
          this.refreshWrapperDisplayData(projectOption);
          this.cdRef.markForCheck();
        }
      });
  }

  private refreshWrapperDisplayData(wrapper: ProjectOptionDefWrapper): void {
    if (!wrapper) {
      return;
    }
    wrapper.displayName = this.projectOptionUtils.getProjectOptionDisplayName(wrapper.projectOptionDef, this.selectedLangKey);
  }

  public editNotice(): void {
    this.projectComponentPresenter.openConfidentialNoticePopup();
  }

  public onSave(): void {
    this.projectUserModel.projectOptionDefs = this.projectOptionContainer.getAll().map((wrapper: ProjectOptionDefWrapper) => wrapper.projectOptionDef);
    this.projectUserModel.saveProjectOptionDefs();

    this.onCancel();
  }

  public onCancel(): void {
    this.finish.emit();
    this.bsModalRef.hide();
  }
}
